export enum SkillType {
    Languages = 'Languages',
    ToolsAndTechnologies = 'Tools and Technologies',
    Concepts = 'Concepts',
}

export interface SkillData {
    name: string;
    description: string;
    type?: SkillType;
}

const data: SkillData[] = [
    {
        name: 'Kubernetes',
        type: SkillType.ToolsAndTechnologies,
        description:
            "I was tasked with standing up one of the first Kubernetes clusters at iStreamPlanet — before EKS existed, building it from scratch with kops. Learning the system's architecture from the ground up is why I'm comfortable developing services against its full feature set.",
    },
    {
        name: 'Docker and Containers',
        type: SkillType.ToolsAndTechnologies,
        description:
            'Containers have been the default way I build and ship software since 2016 — writing and maintaining Docker images for everything from Python microservices on Kubernetes to Go services I migrated from AWS Lambda onto ECS.',
    },
    {
        name: 'Metric Collection',
        type: SkillType.Concepts,
        description:
            'I co-wrote an early metrics library that shipped fixed-size payloads to an InfluxDB server that also hosted Grafana for visualization. We later replaced it with a Datadog StatsD integration — a staple of my monitoring and alerting at most positions since.',
    },
    {
        name: 'Kafka',
        type: SkillType.ToolsAndTechnologies,
        description:
            "Kafka served as the message bus for a suite of services that aggregate and schedule event-driven data. Every microservice communicated asynchronously through Kafka, so an outage in any one service didn't stall the pipeline — work simply resumed where it left off.",
    },
    {
        name: 'Uber Cadence',
        type: SkillType.ToolsAndTechnologies,
        description:
            'Cadence was one of my first experiences with a workflow engine outside a managed service. It changed how I design long-running processes and drilled into the importance of writing idempotent steps — experience that carries straight over to its successor, Temporal, a direct fork with the same workflow model.',
    },
    {
        name: 'Apache Airflow',
        type: SkillType.ToolsAndTechnologies,
        description:
            'We used Airflow for operational tasks such as rolling kernel upgrades across our on-premises fleet. By breaking work into small, re-entrant steps that resume where they left off, we avoided many potential incidents.',
    },
    {
        name: 'Asynchronous Architecture',
        type: SkillType.Concepts,
        description:
            "I've built asynchronous designs around SQS, Kafka, and other queues: microservices listen for commands to execute, so producers can enqueue work even while a consumer is down, and it runs once the service recovers. Routing internal tooling through the same message bus made the pattern a default in my service design.",
    },
    {
        name: 'Amazon Web Services',
        type: SkillType.ToolsAndTechnologies,
        description:
            "I've worked extensively with AWS since 2016, proficient across EC2, VPC networking, and Lambda, and comfortable incorporating SNS topics and SQS queues into service designs.",
    },
    {
        name: 'Google Cloud Platform',
        type: SkillType.ToolsAndTechnologies,
        description:
            "Not as extensive as my AWS experience, but I've worked with GCP-specific technologies in past architectures — automating Datadog integration with Pub/Sub, replicating S3 buckets to Cloud Storage, and configuring Cloud CDN.",
    },
    {
        name: 'PostgreSQL',
        type: SkillType.ToolsAndTechnologies,
        description:
            "PostgreSQL is my database of choice. Most recently I've used it for standard relational work, but I've also leaned hard on JSONB for rapidly evolving data: anything query-essential gets promoted to its own column, while the rest stays flexible in the document.",
    },
    {
        name: 'CI/CD',
        type: SkillType.Concepts,
        description:
            "I've set up, configured, and maintained pipelines across GitHub Actions, TeamCity, Jenkins, CodeFresh, and CircleCI — including integrations with AWS CodeDeploy, Kubernetes, and AWS Fargate.",
    },
    {
        name: 'Linux Stack',
        type: SkillType.Concepts,
        description:
            "My development has been Linux-based from 2016 to the present, with most services I've written optimized for containerized environments.",
    },
    {
        name: 'Microsoft Stack',
        type: SkillType.Concepts,
        description:
            'From 2011 to 2016, my development was heavily Microsoft-stack. I spent a lot of time diagnosing the errors our all-Microsoft CI/CD toolchain, built around Visual Studio, produced, and at my first job I owned the PowerShell setup script — checking dependencies, deploying the software, configuring IIS and SQL Server, and handling upgrades from prior versions.',
    },
    {
        name: 'REST Web Services',
        type: SkillType.Concepts,
        description:
            "I've built many web services to a proper RESTful standard — resource-oriented design, appropriate status codes, and OpenAPI documentation.",
    },
    {
        name: 'Multi-Region Services',
        type: SkillType.Concepts,
        description:
            "I've helped design and implement services spanning multiple regions — data replication, request routing across domains and zones, and region-specific resource allocation.",
    },
    {
        name: 'Infrastructure as Code',
        type: SkillType.Concepts,
        description:
            'I use Pulumi to automate all the infrastructure for this website — and the same tooling to spin up and tear down personal services on demand, including Jitsi, virtual tabletops, and various game servers.',
    },
    {
        name: 'C#',
        type: SkillType.Languages,
        description:
            'I added features to and maintained an Excel add-in with heavy COM interop — including the timeless fun of figuring out when a VBA array is zero- or one-based. I later took over our setup script, tracking issues from mismatched DLLs down to the root cause with a disassembler.',
    },
    {
        name: 'Java',
        type: SkillType.Languages,
        description:
            "I've used Java extensively for web services. On the DRM team at iStreamPlanet, our services were built for high throughput — sustaining hundreds of requests per second.",
    },
    {
        name: 'Python',
        type: SkillType.Languages,
        description:
            'I built a suite of five Python microservices that communicated over queues, with pyenv and Pipenv managing the environments in Docker containers deployed on Kubernetes.',
    },
    {
        name: 'Go',
        type: SkillType.Languages,
        description:
            "I've used Go heavily since 2017 and am typically the go-to person on my teams for the language's finer details. I've built everything from web services to long-running data-processing pipelines.",
    },
    {
        name: 'TypeScript',
        type: SkillType.Languages,
        description:
            'My main TypeScript experience is in AWS serverless architecture — chaining Lambdas together and orchestrating workflows with Step Functions — and in building and maintaining this site, which is TypeScript and Vue.',
    },
    {
        name: 'AI Tooling',
        type: SkillType.ToolsAndTechnologies,
        description:
            "I've worked AI tooling into my day-to-day development. At Tanium I leveraged Claude Code for code verification and a variety of development workflows — using it to accelerate the work without outsourcing the engineering judgment.",
    },
];

export default data;
